/**
 * Unpublished Manager Utility
 * Manages prayers and items explicitly unpublished by admins.
 * Provides functions to track, add, remove, and list unpublished prayers.
 */

const UNPUBLISHED_PREFIX = 'gpn_unpublished_';

export const UnpublishedManager = {
  /**
   * Get all unpublished items or IDs for a module
   */
  getUnpublishedItems: (moduleKey) => {
    try {
      const key = `${UNPUBLISHED_PREFIX}${moduleKey}`;
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch (e) {
      console.error('Error getting unpublished items:', e);
      return [];
    }
  },

  /**
   * Get list of unpublished IDs
   */
  getUnpublishedIds: (moduleKey) => {
    const items = UnpublishedManager.getUnpublishedItems(moduleKey);
    return items.map((i) => (typeof i === 'object' && i !== null ? String(i.id) : String(i)));
  },

  /**
   * Check if an item is unpublished
   */
  isUnpublished: (moduleKey, itemId) => {
    const ids = UnpublishedManager.getUnpublishedIds(moduleKey);
    return ids.includes(String(itemId));
  },

  /**
   * Mark an item as unpublished
   */
  addUnpublished: (moduleKey, item) => {
    try {
      const key = `${UNPUBLISHED_PREFIX}${moduleKey}`;
      const items = UnpublishedManager.getUnpublishedItems(moduleKey);
      const itemId = typeof item === 'object' && item !== null ? item.id : item;
      const exists = items.some((i) => String(typeof i === 'object' ? i.id : i) === String(itemId));
      if (!exists) {
        const entry = typeof item === 'object' && item !== null
          ? { ...item, status: 'inactive', unpublishedAt: new Date().toISOString() }
          : { id: item, status: 'inactive', unpublishedAt: new Date().toISOString() };
        const updated = [entry, ...items];
        localStorage.setItem(key, JSON.stringify(updated));
        return { success: true, items: updated };
      }
      return { success: true, items };
    } catch (e) {
      console.error('Error adding unpublished item:', e);
      return { success: false, message: 'Failed to record unpublished item' };
    }
  },

  /**
   * Remove an item from unpublished (when re-published)
   */
  removeUnpublished: (moduleKey, itemId) => {
    try {
      const key = `${UNPUBLISHED_PREFIX}${moduleKey}`;
      const items = UnpublishedManager.getUnpublishedItems(moduleKey);
      const updated = items.filter((i) => String(typeof i === 'object' ? i.id : i) !== String(itemId));
      localStorage.setItem(key, JSON.stringify(updated));
      return { success: true, items: updated };
    } catch (e) {
      console.error('Error removing unpublished item:', e);
      return { success: false, message: 'Failed to remove from unpublished' };
    }
  },
};

export default UnpublishedManager;
